import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Card,
  CardContent,
  EmptyState,
  FullscreenSpinner,
  Input,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  useToast,
} from '@shared/ui';
import { CATEGORY_KINDS, taxonomyApi, type Category, type CategoryKind } from '@entities/taxonomy';
import { useCurrentLocale } from '@shared/i18n';
import { ROUTES } from '@shared/config/routes';
import { Link } from 'react-router-dom';
import { CategoryTree } from './category-tree';
import {
  KIND_HINT,
  KIND_LABEL,
  buildForest,
  collectIds,
  filterForest,
} from './category-model';
import {
  CreateCategoryDialog,
  DeleteCategoryDialog,
  EditCategoryDialog,
} from './category-dialogs';

export function CategoriesPage() {
  const [activeKind, setActiveKind] = useState<CategoryKind>('PRODUCT');

  const { data, isLoading } = useQuery({
    queryKey: ['categories'],
    queryFn: () => taxonomyApi.listCategories(),
  });

  const all = useMemo(() => data ?? [], [data]);
  const countByKind = useMemo(() => {
    const counts = { PRODUCER: 0, ECO_PRODUCER: 0, PRODUCT: 0 } as Record<CategoryKind, number>;
    for (const c of all) counts[c.kind] = (counts[c.kind] ?? 0) + 1;
    return counts;
  }, [all]);

  if (isLoading) return <FullscreenSpinner />;

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Категории</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Три независимых дерева. Slug уникален внутри своего типа — один и тот же slug может
          существовать и в товарах, и у производителей.
        </p>
      </div>

      <Tabs value={activeKind} onValueChange={(v) => setActiveKind(v as CategoryKind)}>
        <TabsList>
          {CATEGORY_KINDS.map((kind) => (
            <TabsTrigger key={kind} value={kind} className="gap-2">
              {KIND_LABEL[kind]}
              <span className="rounded bg-muted-foreground/15 px-1.5 py-0.5 text-[10px] tabular-nums">
                {countByKind[kind] ?? 0}
              </span>
            </TabsTrigger>
          ))}
        </TabsList>

        {CATEGORY_KINDS.map((kind) => (
          <TabsContent key={kind} value={kind}>
            <KindPanel kind={kind} categories={all.filter((c) => c.kind === kind)} />
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}

function KindPanel({ kind, categories }: { kind: CategoryKind; categories: Category[] }) {
  const locale = useCurrentLocale();
  const qc = useQueryClient();
  const toast = useToast();

  const [query, setQuery] = useState('');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [reorderingId, setReorderingId] = useState<string | null>(null);

  const [createFor, setCreateFor] = useState<{ parent: Category | null } | null>(null);
  const [editing, setEditing] = useState<Category | null>(null);
  const [deleting, setDeleting] = useState<Category | null>(null);

  const forest = useMemo(() => buildForest(categories), [categories]);
  const visible = useMemo(() => filterForest(forest, query), [forest, query]);

  // While searching, everything stays open so matches deep in the tree are reachable.
  const searching = query.trim().length > 0;
  const effectiveExpanded = useMemo(
    () => (searching ? new Set(collectIds(visible)) : expanded),
    [searching, visible, expanded],
  );

  const toggle = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const expandAll = () => setExpanded(new Set(collectIds(forest)));
  const collapseAll = () => setExpanded(new Set());

  /** Swaps sortOrder with the adjacent sibling; two PUTs, then refetch. */
  const move = useMutation({
    mutationFn: async ({
      category,
      direction,
      siblings,
    }: {
      category: Category;
      direction: -1 | 1;
      siblings: Category[];
    }) => {
      const index = siblings.findIndex((c) => c.id === category.id);
      const neighbour = siblings[index + direction];
      if (!neighbour) return;
      // Equal sortOrder values would make the swap a no-op — nudge them apart.
      const a = category.sortOrder;
      const b = neighbour.sortOrder === a ? a + direction : neighbour.sortOrder;
      await taxonomyApi.updateCategory(category.id, { name: category.name, sortOrder: b });
      await taxonomyApi.updateCategory(neighbour.id, { name: neighbour.name, sortOrder: a });
    },
    onMutate: ({ category }) => setReorderingId(category.id),
    onSettled: () => setReorderingId(null),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['categories'] }),
    onError: (e: Error) => toast.error('Не удалось изменить порядок', e.message),
  });

  const isEmpty = categories.length === 0;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <Input
          placeholder="Поиск по названию или slug…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="h-9 max-w-xs"
        />
        {searching ? (
          <Button variant="ghost" size="sm" onClick={() => setQuery('')}>
            Сбросить
          </Button>
        ) : (
          <>
            <Button variant="ghost" size="sm" onClick={expandAll}>
              Развернуть всё
            </Button>
            <Button variant="ghost" size="sm" onClick={collapseAll}>
              Свернуть всё
            </Button>
          </>
        )}
        <div className="ml-auto flex items-center gap-2">
          <Link to={ROUTES.taxonomyHsCodes} className="text-xs text-primary hover:underline">
            HS-коды →
          </Link>
          <Button size="sm" onClick={() => setCreateFor({ parent: null })}>
            + Корневая категория
          </Button>
        </div>
      </div>

      <p className="text-xs text-muted-foreground">{KIND_HINT[kind]}</p>

      <Card>
        <CardContent className="p-2">
          {isEmpty ? (
            <EmptyState
              title="Здесь пока пусто"
              description={`В дереве «${KIND_LABEL[kind]}» ещё нет категорий.`}
              action={
                <Button onClick={() => setCreateFor({ parent: null })}>
                  Создать первую категорию
                </Button>
              }
              className="border-0"
            />
          ) : visible.length === 0 ? (
            <EmptyState
              title="Ничего не найдено"
              description={`По запросу «${query}» совпадений нет.`}
              className="border-0"
            />
          ) : (
            <CategoryTree
              forest={visible}
              locale={locale}
              expanded={effectiveExpanded}
              onToggle={toggle}
              reorderingId={reorderingId}
              actions={{
                onAddChild: (parent) => setCreateFor({ parent }),
                onEdit: setEditing,
                onDelete: setDeleting,
                onMove: (category, direction, siblings) =>
                  move.mutate({ category, direction, siblings }),
              }}
            />
          )}
        </CardContent>
      </Card>

      {createFor ? (
        <CreateCategoryDialog
          open
          onOpenChange={(o) => !o && setCreateFor(null)}
          kind={kind}
          parent={createFor.parent}
          allOfKind={categories}
        />
      ) : null}

      <EditCategoryDialog
        open={!!editing}
        onOpenChange={(o) => !o && setEditing(null)}
        category={editing}
      />

      {deleting ? (
        <DeleteCategoryDialog category={deleting} onOpenChange={(o) => !o && setDeleting(null)} />
      ) : null}
    </div>
  );
}
